import React, { Fragment, Suspense, useContext, useRef, useState } from 'react';
import {
  belowBreakpoint,
  Box,
  ButtonAppearance,
  FormControl,
  IconSize,
  Select,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  Theme,
  ToastAction,
  ToastType,
  useMediaQuery,
  useToastsDispatch,
  theme,
} from '@bechtel/gds';
import {
  UpdateWorkPackageDisciplineInput,
  UpdateWorkPackageDisciplinesPayload,
  WorkPackageDiscipline,
} from '@bechtel/types';
import { NetworkStatus, useQuery, useMutation } from '@apollo/client';
import { Formik } from 'formik';
import { filter, find, get, map, noop } from 'lodash';
import { ThemeContext } from 'styled-components';
import {
  SuspenseLoader,
  useAppUser,
  ErrorBoundary,
  ErrorRetry,
  useEnvProvider,
  ProjectAppTypes,
} from '@bechtel/client-common';
import { UPDATE_WORKPACKAGE_DISCIPLINES, GET_PROJECT_WORK_PACKAGE_DISCIPLINES_WITH_STATUS } from '@bechtel/client-gql';
import { UpdateDisciplinesRequestError } from './error/types';
import { DisciplineList } from './list';
import {
  DisciplineHeadingStyled,
  DisciplinesContainerStyled,
  DisciplinesFooterButtonStyled,
  DisciplinesInnerBlockStyled,
  DisciplinesSubContainerStyled,
  FooterButtonStyled,
  SearchFieldComponentStyled,
  SearchFieldStyled,
} from './styled';
import UpdateDisciplinesRequestErrorModal from '~/work-package-admin/disciplines/error';
import RemoveDisciplinesModal from '~/work-package-admin/disciplines/confirm-modal';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

export interface SortBy {
  id: boolean | 'asc' | 'desc';
  name: string;
}

export interface FormikState {
  disciplines: WorkPackageDiscipline[];
}

const DisciplineUpdateToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Work Package Disciplines saved successfully.</Trans>
      </Text>
    </Box>
  );
};

const DropdownSelectedItem = ({ item }: { item: SortBy }) => {
  const { i18n } = useLingui();
  return (
    <Text family="graphik" size="400" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
      {i18n._(item.name)}
    </Text>
  );
};
const DropdownItem = ({ item }: { item: SortBy }) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
      {i18n._(item.name)}
    </Text>
  );
};

let initialValues: FormikState | undefined = undefined;
const sortByValues: SortBy[] = [
  {
    id: 'asc',
    name: t`Discipline (A-Z)`,
  },
  {
    id: 'desc',
    name: t`Discipline (Z-A)`,
  },
];
export const WorkPackageDisciplines = () => {
  const { user } = useAppUser();
  const { envConfig } = useEnvProvider();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const dispatchToast = useToastsDispatch();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [sort, setSort] = useState<SortBy>(sortByValues[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const changedDisciplinesRef = useRef<WorkPackageDiscipline[]>([]);
  const [
    updateDisciplinesRequestError,
    setUpdateDisciplinesRequestError,
  ] = useState<UpdateDisciplinesRequestError | null>();
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  const closeModal = () => {
    setUpdateDisciplinesRequestError(null);
  };
  const [updateWorkpackageDisciplines, { loading: updateDisciplinesLoading }] = useMutation<
    { updateDisciplines: UpdateWorkPackageDisciplinesPayload },
    { input: UpdateWorkPackageDisciplineInput[] }
  >(UPDATE_WORKPACKAGE_DISCIPLINES, {
    onCompleted: (data: { updateDisciplines: UpdateWorkPackageDisciplinesPayload }) => {
      const updateDisciplinesRequestError = get(data, 'updateDisciplines.error');

      if (updateDisciplinesRequestError) {
        setUpdateDisciplinesRequestError(updateDisciplinesRequestError);
      } else {
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: DisciplineUpdateToast,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
      }
    },
    onError: (error) => {
      setUpdateDisciplinesRequestError(error);
    },
  });

  const { loading, error, data, refetch, networkStatus } = useQuery<{
    workPackageDisciplines: WorkPackageDiscipline[];
  }>(GET_PROJECT_WORK_PACKAGE_DISCIPLINES_WITH_STATUS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    variables: {
      id: user?.project?.id,
    },
  });
  const disciplinesArr: WorkPackageDiscipline[] = get(data, 'projectConfigById.workPackageDisciplines', []);
  initialValues = {
    disciplines: disciplinesArr,
  };

  const saveDisciplines = (changedDisciplines: WorkPackageDiscipline[]) => {
    updateWorkpackageDisciplines({
      variables: {
        input: map(changedDisciplines, (discipline) => {
          return {
            id: discipline.id,
            status: discipline.isActive,
          };
        }),
      },
    });
  };

  const onFormSubmit = (values: FormikState) => {
    const changedDisciplines = filter(values.disciplines, (discipline) => {
      return (
        find(
          initialValues?.disciplines,
          (initialDiscipline) =>
            initialDiscipline.id == discipline.id && initialDiscipline.isActive != discipline.isActive
        ) != null
      );
    });
    if (!changedDisciplines || changedDisciplines.length == 0) return;
    changedDisciplinesRef.current = changedDisciplines;

    const removedDisciplines = filter(changedDisciplines, (discipline) => !discipline.isActive);
    if (removedDisciplines && removedDisciplines.length > 0) setShowDeleteConfirmModal(true);
    else saveDisciplines(changedDisciplines);
  };

  const activeCount = filter(disciplinesArr, (inner) => inner.isActive).length;
  return (
    <Fragment>
      <DisciplinesContainerStyled>
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!loading && error && (
          <div data-testid="error-retry">
            <ErrorRetry refetch={refetch}>
              <Trans>
                There was a problem loading Work Package Disciplines list. Please click the button below to try again.
              </Trans>
            </ErrorRetry>
          </div>
        )}
        {!loading && !error && disciplinesArr.length == 0 && (
          <div data-testid="not-found">
            <Trans>No Disciplines found.</Trans>
          </div>
        )}

        {initialValues && !loading && !error && (
          <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={noop}>
            {(form) => {
              const { dirty, isValid, values } = form;
              return (
                <>
                  <DisciplinesSubContainerStyled flexDirection={{ sm: 'column' }}>
                    <DisciplineHeadingStyled>
                      <Box flexBasis={{ sm: '50%' }} mb={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
                        <Box mb={{ sm: '450' }}>
                          <Text family="graphik" size="500" weight="600">
                            <Trans>Work Package Disciplines</Trans> ({activeCount} <Trans>Selected</Trans>)
                          </Text>
                        </Box>

                        <Text
                          family="graphik"
                          size="300"
                          color="grey.1000"
                          style={{ lineHeight: `${theme.spacings[550]}` }}
                        >
                          {t`This list shows the disciplines available in TEAMWorks. Select a discipline to import its
                            associated Work Packages into ${isPFAppType ? 'PipeFighter' : 'ArcStryker'}.`}
                        </Text>
                      </Box>
                      <Box
                        flexBasis={{ sm: '50%' }}
                        pt={{ sm: '550' }}
                        justifyContent={{ sm: 'flex-end' }}
                        alignItems={{ sm: 'flex-start' }}
                      >
                        <SearchFieldStyled>
                          <SearchFieldComponentStyled
                            size={TextFieldSize.LARGE}
                            iconSize={IconSize.MEDIUM}
                            placeholder={t`Search`}
                            type="search"
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                            }}
                            value={searchQuery}
                          />
                        </SearchFieldStyled>

                        <FormControl
                          label={t`Sort By`}
                          css={`
                            width: 200px;
                          `}
                        >
                          <Select
                            name="sort"
                            size={isWideScreen ? TextFieldSize.MEDIUM : TextFieldSize.LARGE}
                            appearance={TextFieldAppearance.NORMAL}
                            items={sortByValues}
                            selectedItem={sort}
                            onSelectedItemChange={(changes: any) => {
                              setSort(changes.selectedItem);
                            }}
                            itemComponent={DropdownItem}
                            selectedComponent={DropdownSelectedItem}
                          />
                        </FormControl>
                      </Box>
                    </DisciplineHeadingStyled>
                    <DisciplinesInnerBlockStyled>
                      <DisciplineList form={form} sort={sort} search={searchQuery?.trim()} />
                    </DisciplinesInnerBlockStyled>
                  </DisciplinesSubContainerStyled>
                  {!loading && !error && (
                    <FooterButtonStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <DisciplinesFooterButtonStyled
                            disabled={updateDisciplinesLoading || !dirty}
                            onClick={() => {
                              form.handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </DisciplinesFooterButtonStyled>
                        </Box>
                        <DisciplinesFooterButtonStyled
                          onClick={() => {
                            onFormSubmit(values);
                          }}
                          disabled={updateDisciplinesLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {updateDisciplinesLoading && <Spinner />}
                          {!updateDisciplinesLoading && t`Save Changes`}
                        </DisciplinesFooterButtonStyled>
                      </Box>
                    </FooterButtonStyled>
                  )}
                  {showDeleteConfirmModal && (
                    <ErrorBoundary>
                      <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                        <RemoveDisciplinesModal
                          disciplinesToRemove={filter(
                            changedDisciplinesRef.current,
                            (discipline) => !discipline.isActive
                          )}
                          onCancel={() => {
                            setShowDeleteConfirmModal(false);
                          }}
                          onConfirm={() => {
                            setShowDeleteConfirmModal(false);
                            saveDisciplines(changedDisciplinesRef.current);
                          }}
                        />
                      </Suspense>
                    </ErrorBoundary>
                  )}
                </>
              );
            }}
          </Formik>
        )}
      </DisciplinesContainerStyled>
      {updateDisciplinesRequestError && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <UpdateDisciplinesRequestErrorModal
            updateDisciplinesError={updateDisciplinesRequestError}
            close={closeModal}
          />
        </Suspense>
      )}
    </Fragment>
  );
};
